import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Star, Play, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import avatarAlexPark from "@/assets/avatar-alex-park.png";
import avatarJamesAdeyemi from "@/assets/avatar-james-adeyemi.png";
import avatarDanielOkoro from "@/assets/avatar-daniel-okoro.png";
import avatarRyanCooper from "@/assets/avatar-ryan-cooper.png";

type Platform = "youtube" | "instagram" | "tiktok" | "linkedin" | "twitter";
type Category = "all" | "youtube" | "instagram" | "tiktok" | "coaches";

interface Review {
  name: string;
  bio: string;
  platform: Platform;
  quote: string;
  hasVideo: boolean;
  highlight: boolean;
  date: string;
  avatarColor: string;
  avatarLetter: string;
  avatarImage?: string;
  category: Category;
}

const reviews: Review[] = [
  { name: "Jas Kaur", bio: "Fitness Creator · 1,200 followers", platform: "instagram", quote: "I had zero idea what to sell. AI StoryCraft literally built my first digital product for me — a fitness meal plan. I made $1,247 in 14 days. I cried. Actual tears.", hasVideo: true, highlight: false, date: "March 12, 2026", avatarColor: "#E74C3C", avatarLetter: "J", category: "instagram" },
  { name: "Alex Park", bio: "Education creator · runs online tutoring", platform: "youtube", quote: "Upgraded to the annual Creator plan during that special offer and it's the best money I've spent on my business this year.\n\n5 AI tools. Monthly Q&A calls. Viral hook library.\n\nIt replaced like 3 separate subscriptions I was paying for.\n\nThe annual price is a no-brainer if you're serious about this.", hasVideo: false, highlight: false, date: "February 2026", avatarColor: "#2563EB", avatarLetter: "A", avatarImage: avatarAlexPark, category: "youtube" },
  { name: "Kemi Adeyemi", bio: "Food Creator · 890 followers", platform: "tiktok", quote: "I've been posting recipes for 2 years and never made a penny from it. AI StoryCraft helped me turn my most-saved recipe into an ebook. $640 in 3 weeks. From 890 followers. I still can't believe it.", hasVideo: true, highlight: true, date: "February 28, 2026", avatarColor: "#9B59B6", avatarLetter: "K", category: "tiktok" },
  { name: "James Adeyemi", bio: "Tech reviewer · 12K subs", platform: "youtube", quote: "Switched from Jasper to AI StoryCraft and honestly wish I'd done it sooner.\n\nThe keyword research actually finds topics people are searching for, not just random ideas. My last 5 videos all ranked page one.\n\nAnd the price? $69/mo for all of this is mad.", hasVideo: false, highlight: false, date: "March 2026", avatarColor: "#0EA5E9", avatarLetter: "J", avatarImage: avatarJamesAdeyemi, category: "youtube" },
  { name: "Daniel Osei", bio: "Lifestyle Creator · 520 followers", platform: "instagram", quote: "I only had 520 followers. I genuinely thought I was too small. The platform showed me I wasn't. $480 in week one from a mindset journal template. Game changer.", hasVideo: true, highlight: false, date: "February 15, 2026", avatarColor: "#3498DB", avatarLetter: "D", category: "instagram" },
  { name: "Gabriel Santos", bio: "Marketing Coach · 14,000 followers", platform: "youtube", quote: "One product. One video. $4,200. I wish I'd found AI StoryCraft 2 years ago. The Nexis AI script tool alone is worth the subscription.", hasVideo: true, highlight: false, date: "February 10, 2026", avatarColor: "#2ECC71", avatarLetter: "G", category: "youtube" },
  { name: "Daniel Okoro", bio: "Finance creator · 14K YouTube subscribers", platform: "youtube", quote: "Been using AI StoryCraft for about 3 months now.\n\nNexis AI writes my scripts. Keyword Research finds my topics. Clipping AI turns one video into 6 shorts.\n\nWhat used to take me a full day now takes about 90 minutes.\n\nThe Creator plan basically pays for itself after your first week.", hasVideo: false, highlight: true, date: "January 2026", avatarColor: "#16A085", avatarLetter: "D", avatarImage: avatarDanielOkoro, category: "youtube" },
  { name: "Marcus Webb", bio: "Finance Creator · 8,200 followers", platform: "tiktok", quote: "The Clip AI tool turned my 20-minute breakdown into 6 viral shorts. Two of them hit 100K+ views. My email list doubled in a week.", hasVideo: true, highlight: false, date: "January 28, 2026", avatarColor: "#8E44AD", avatarLetter: "M", category: "tiktok" },
  { name: "Ryan Cooper", bio: "Fitness creator · runs a PT business", platform: "instagram", quote: "The Clipping AI is stupid good. I uploaded a 40 minute podcast and it pulled out 8 clips with captions already on them.\n\nPosted them across TikTok, Reels, and Shorts and one of them hit 50K views.\n\nThat would've taken my editor a full day.\n\nGenuinely can't go back to doing it manually.", hasVideo: false, highlight: false, date: "February 2026", avatarColor: "#E91E63", avatarLetter: "R", avatarImage: avatarRyanCooper, category: "instagram" },
  { name: "Ryan Torres", bio: "Fitness YouTuber · 11,000 followers", platform: "youtube", quote: "Nexis AI wrote me a script that outperformed anything I'd ever done. 47K views on a channel that usually gets 3K. The algorithm loved it.", hasVideo: true, highlight: false, date: "January 15, 2026", avatarColor: "#C0392B", avatarLetter: "R", category: "youtube" },
  { name: "Amara Johnson", bio: "Style Creator · 1,600 followers", platform: "tiktok", quote: "I turned my 'outfit of the day' content into a capsule wardrobe guide. The AI built the whole sales page for me. $720 in the first 10 days. With 1,600 followers.", hasVideo: false, highlight: true, date: "January 9, 2026", avatarColor: "#D35400", avatarLetter: "A", category: "tiktok" },
  { name: "Liam Fletcher", bio: "Photography Educator · 5,300 followers", platform: "youtube", quote: "I was sceptical about AI tools but the Email List Builder changed my mind. Grew my list by 800 subscribers in 3 weeks and launched a preset pack that made $2,400.", hasVideo: true, highlight: false, date: "January 3, 2026", avatarColor: "#2980B9", avatarLetter: "L", category: "youtube" },
  { name: "Nina Alvarez", bio: "Parenting Creator · 3,100 followers", platform: "instagram", quote: "Never thought my 'mum content' could make money. AI StoryCraft showed me exactly how to package my routines into a guide. $890 in two weeks. My husband was speechless.", hasVideo: true, highlight: false, date: "December 28, 2025", avatarColor: "#27AE60", avatarLetter: "N", category: "instagram" },
  { name: "Tom Bradley", bio: "Music Producer · 7,400 followers", platform: "youtube", quote: "Used the Sales Page Builder to launch my sample pack. The copy it wrote converted at 8.2%. That's insane for a first launch. $1,650 in 5 days.", hasVideo: true, highlight: false, date: "December 20, 2025", avatarColor: "#7F8C8D", avatarLetter: "T", category: "youtube" },
  { name: "Zara Okonkwo", bio: "Career Coach · 2,200 followers", platform: "linkedin", quote: "The Launch Advisor mapped out my entire product launch week by week. I followed it exactly and hit $4,800 in my first month. It felt like having a co-founder.", hasVideo: true, highlight: false, date: "December 14, 2025", avatarColor: "#F1C40F", avatarLetter: "Z", category: "coaches" },
  { name: "Chris Nguyen", bio: "Gaming Creator · 9,800 followers", platform: "tiktok", quote: "Turned my Twitch highlights into short-form content with Clip AI. One clip hit 1.2M views. Used that momentum to sell a gaming guide — $2,100 in a week.", hasVideo: true, highlight: false, date: "December 7, 2025", avatarColor: "#1ABC9C", avatarLetter: "C", category: "tiktok" },
  { name: "Emily Richards", bio: "Yoga Instructor · 950 followers", platform: "instagram", quote: "Under 1,000 followers and I made $560 selling a morning routine guide. The platform made me realise my small audience was actually my superpower.", hasVideo: true, highlight: true, date: "November 30, 2025", avatarColor: "#E91E63", avatarLetter: "E", category: "instagram" },
  { name: "Jake Morrison", bio: "Business Strategy · 18,000 followers", platform: "youtube", quote: "I've tried every AI tool out there. StoryCraft is the only one that actually understands creators. The monetisation suggestions were spot-on. $6,200 from one funnel.", hasVideo: true, highlight: false, date: "November 22, 2025", avatarColor: "#34495E", avatarLetter: "J", category: "coaches" },

];

const platformIcon = (p: Platform) => {
  switch (p) {
    case "youtube": return <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#FF0000" }}><Play className="w-3.5 h-3.5 text-white fill-white" /></div>;
    case "instagram": return <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }}><svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5"/></svg></div>;
    case "tiktok": return <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#000" }}><span className="text-white text-xs font-bold">♪</span></div>;
    case "linkedin": return <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#0A66C2" }}><span className="text-white text-xs font-bold">in</span></div>;
    case "twitter": return <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#000" }}><span className="text-white text-xs font-bold">𝕏</span></div>;
  }
};

const filters: { label: string; value: Category }[] = [
  { label: "All", value: "all" },
  { label: "YouTube Creators", value: "youtube" },
  { label: "Instagram Creators", value: "instagram" },
  { label: "TikTok Creators", value: "tiktok" },
  { label: "Coaches & Educators", value: "coaches" },
];

const INITIAL_COUNT = 12;
const LOAD_MORE_COUNT = 6;

export default function Reviews() {
  const [activeFilter, setActiveFilter] = useState<Category>("all");
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const filtered = activeFilter === "all" ? reviews : reviews.filter(r => r.category === activeFilter);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <>
      <Helmet>
        <title>Creator Reviews & Results | AI StoryCraft Studio</title>
        <meta name="description" content="See how 4,200+ creators use AI StoryCraft Studio to monetise their audiences. Real results, real reviews." />
        <meta property="og:title" content="Creator Reviews & Results | AI StoryCraft Studio" />
        <meta property="og:description" content="See how 4,200+ creators use AI StoryCraft Studio to monetise their audiences. Real results, real reviews." />
        <link rel="canonical" href="https://ai-storycraft-studio.com/reviews" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-20" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F0FF 100%)" }}>
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <ScrollReveal>
            <h1 className="font-display text-foreground mb-4" style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, lineHeight: 1.15 }}>
              Trusted by 4,200+ Creators.{" "}
              <span className="text-primary">Here's What They Say.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mb-8 max-w-xl mx-auto">
              Real results from real creators using AI StoryCraft Studio to monetise their audiences.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <Button size="lg" onClick={() => window.location.href = "/pricing"}>
                Start Your Free Trial →
              </Button>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/5">
                ♥ Share Your Story
              </Button>
            </div>
            <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />)}
              <span className="ml-1.5 font-medium">4.9/5 from 847 reviews</span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="bg-muted border-y border-border">
        <div className="container mx-auto px-4 py-5 flex flex-wrap justify-center gap-6 md:gap-12">
          {[
            { label: "$2.1M+ generated by creators" },
            { label: "4,200+ active creators" },
            { label: "4.9/5 average rating" },
          ].map((s, i) => (
            <span key={i} className="text-sm md:text-base font-semibold text-foreground">{s.label}</span>
          ))}
        </div>
      </div>

      {/* Filters + Grid */}
      <section className="py-12 md:py-16 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Filter Pills */}
          <div className="flex gap-2 mb-10 overflow-x-auto pb-2 scrollbar-hide">
            {filters.map(f => (
              <button
                key={f.value}
                onClick={() => { setActiveFilter(f.value); setVisibleCount(INITIAL_COUNT); }}
                className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={{
                  background: activeFilter === f.value ? "hsl(var(--primary))" : "hsl(var(--card))",
                  color: activeFilter === f.value ? "#fff" : "hsl(var(--muted-foreground))",
                  border: activeFilter === f.value ? "none" : "1px solid hsl(var(--border))",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Masonry Grid */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5">
            {visible.map((r, i) => (
              <ScrollReveal key={`${r.name}-${i}`} delay={i % 3 * 80}>
                <div
                  className="break-inside-avoid rounded-2xl p-6 bg-card"
                  style={{
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    borderLeft: r.highlight ? "3px solid hsl(var(--primary))" : "none",
                  }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {r.avatarImage ? (
                        <img
                          src={r.avatarImage}
                          alt={r.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                          style={{ background: r.avatarColor }}
                        >
                          {r.avatarLetter}
                        </div>
                      )}
                      <div>
                        <p className="text-foreground font-semibold text-sm">{r.name}</p>
                        <p className="text-muted-foreground text-xs">{r.bio}</p>
                      </div>
                    </div>
                    {platformIcon(r.platform)}
                  </div>

                  {/* Quote */}
                  <div className="text-foreground leading-relaxed mb-4" style={{ fontSize: "1.05rem" }}>
                    {r.quote.split('\n\n').map((paragraph, pi) => (
                      <p key={pi} className={pi > 0 ? "mt-3" : ""}>
                        {pi === 0 ? `"${paragraph}` : paragraph}
                        {pi === r.quote.split('\n\n').length - 1 ? '"' : ''}
                      </p>
                    ))}
                  </div>

                  {/* Video Placeholder */}
                  {r.hasVideo && (
                    <div
                      className="rounded-xl mb-4 flex items-center justify-center overflow-hidden"
                      style={{
                        aspectRatio: "16/9",
                        background: "linear-gradient(135deg, hsl(var(--primary)), #9F67FF)",
                      }}
                    >
                      <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-6 h-6 text-primary fill-primary ml-1" />
                      </div>
                    </div>
                  )}

                  {/* Date */}
                  <p className="text-muted-foreground text-xs">{r.date}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center mt-10">
              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary/5 w-full md:w-auto"
                onClick={() => setVisibleCount(v => v + LOAD_MORE_COUNT)}
              >
                Load More Reviews
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-20" style={{ background: "linear-gradient(135deg, hsl(var(--primary)), #9333EA)" }}>
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-white font-display font-bold text-2xl md:text-4xl mb-4">Ready to Join Them?</h2>
          <p className="text-white/80 mb-8 text-base md:text-lg">
            Start your 7-day free trial and see what AI StoryCraft Studio can do for your content.
          </p>
          <Button
            size="lg"
            className="bg-white text-primary font-bold hover:bg-white/90 hover:scale-[1.02] transition-all"
            onClick={() => window.location.href = "/pricing"}
          >
            Start Your Free Trial →
          </Button>
          <p className="text-white/60 text-sm mt-4">No credit card required · Cancel anytime</p>
        </div>
      </section>

      <FooterSection />
    </>
  );
}
